if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // Drawer-first keeps section rendering aligned with mini-cart architecture.
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('.dd-atc-button__text') || this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        const cartTarget = document.querySelector('cart-drawer') || this.cart;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (cartTarget) {
          formData.append(
            'sections',
            cartTarget.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cartTarget.setActiveElement(document.activeElement);
        }
        config.body = formData;
        const ddHelpers = window.ddCartHelpers;
        const runAddRequest = (attempt = 0) =>
          fetch(`${routes.cart_add_url}`, config).then((response) => {
            if (response.status === 429 && attempt < 2) {
              return new Promise((resolve) => {
                setTimeout(resolve, 650 * (attempt + 1));
              }).then(() => runAddRequest(attempt + 1));
            }
            return response.json();
          });
        // First-order (SALONTRIAL20) auto-apply: when the trial offer is
        // on the page (dd-pricing-trial-offer.js publishes ddTrialOffer
        // only for eligible zero-order customers), set the discount
        // cookie BEFORE the add so the bundled cart-drawer HTML already
        // shows the discounted line. ensureApplied never rejects and is
        // zero extra requests after the first ATC of the session.
        const trialReady =
          window.ddTrialOffer && typeof window.ddTrialOffer.ensureApplied === 'function'
            ? window.ddTrialOffer.ensureApplied()
            : Promise.resolve(false);
        const addRequest = trialReady.then(() =>
          ddHelpers && typeof ddHelpers.enqueueExternalMutation === 'function'
            ? ddHelpers.enqueueExternalMutation({
                label: 'pdp-add',
                requester: 'product-form',
                endpoint: routes.cart_add_url,
                action: 'pdp-add',
                run: runAddRequest,
                invalidateFreshCart: true,
              })
            : runAddRequest()
        );

        addRequest
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              var helpers = ddHelpers || window.ddCartHelpers;
              var errLike = { shopifyStatus: response.status, description: response.description,
                             responseBody: response };
              var addQty = parseInt(formData.get('quantity'), 10) || 1;
              var stockParsed = helpers && helpers.parseCartStockError(errLike, { operation: 'add', requestedQty: addQty });
              var stockMsg = stockParsed && helpers.stockErrorMessage(stockParsed, { operation: 'add', requestedQty: addQty });
              var displayMsg = (stockMsg && stockMsg.message) || response.description;
              var toastType = (stockMsg && stockMsg.toastType) || 'error';

              this.handleErrorMessage(displayMsg);
              if (window.ddToast) window.ddToast(displayMsg, { type: toastType });

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!cartTarget) {
              window.location = window.routes.cart_url;
              return;
            }

            if (
              response &&
              Array.isArray(response.items) &&
              typeof response.item_count === 'number' &&
              window.ddCartHelpers &&
              typeof window.ddCartHelpers.seedLatestCart === 'function'
            ) {
              window.ddCartHelpers.seedLatestCart(response);
            }

            this.error = false;

            const publishAfterRender = () => {
              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            };

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    try {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        cartTarget.renderContents(response);
                      });
                    } catch (renderErr) {
                      console.error('[product-form] drawer render failed', renderErr);
                      if (window.ddToast) window.ddToast('Added to cart', { type: 'success' });
                    }
                    publishAfterRender();
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              try {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  cartTarget.renderContents(response);
                });
              } catch (renderErr) {
                console.error('[product-form] drawer render failed', renderErr);
                if (window.ddToast) window.ddToast('Added to cart', { type: 'success' });
                if (cartTarget && typeof cartTarget.open === 'function') {
                  try { cartTarget.open(); } catch (_) {}
                }
              }
              publishAfterRender();
            }
          })
          .catch((e) => {
            console.error('[product-form] ATC failed', e);
            if (window.ddToast) window.ddToast('Could not add to cart \u2014 please try again', { type: 'error' });
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (cartTarget && cartTarget.classList.contains('is-empty')) cartTarget.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
